import React, { useState } from 'react';
import { Icons } from '../ui/Icons';
import Modal from '../ui/Modal';
import { MaintenanceRequest, MaintenanceStatus, MaintenanceHistory, StaffMember } from '../../types';
import { database } from '../../services/database';
import { toast } from 'react-hot-toast';

interface MaintenanceDetailModalProps {
    request: MaintenanceRequest;
    onClose: () => void;
    staffName: string;
    staffList: StaffMember[];
}

const MaintenanceDetailModal: React.FC<MaintenanceDetailModalProps> = ({ request, onClose, staffName, staffList }) => {
    const [newStatus, setNewStatus] = useState<MaintenanceStatus>(request.status);
    const [note, setNote] = useState('');
    const [updatedBy, setUpdatedBy] = useState(''); // New state for reporting staff
    const [isUpdating, setIsUpdating] = useState(false);

    const getStatusColor = (status: MaintenanceStatus) => {
        switch (status) {
            case 'Open': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
            case 'In Progress': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
            case 'On Hold': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
            case 'Completed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
            case 'Cancelled': return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
            default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority) {
            case 'Urgent': return 'text-red-600 dark:text-red-400';
            case 'High': return 'text-orange-500 dark:text-orange-400';
            case 'Medium': return 'text-yellow-500 dark:text-yellow-400';
            case 'Low': return 'text-green-600 dark:text-green-400';
            default: return 'text-gray-500 dark:text-gray-400';
        }
    };

    const handleUpdateStatus = async () => {
        if (!updatedBy) {
            toast.error('Select your name to update');
            return;
        }

        if (newStatus === request.status && !note) {
            toast.error('Please select a new status or add a note');
            return;
        }

        setIsUpdating(true);
        try {
            const timestamp = new Date().toISOString();
            const historyItem: MaintenanceHistory = {
                action: newStatus !== request.status ? `Status changed to ${newStatus}` : 'Added note',
                status: newStatus,
                changedBy: updatedBy, // Use the selected staff name
                timestamp,
                note: note.trim()
            };

            const updatedHistory = [...(request.history || []), historyItem];
            
            await database.updateMaintenanceRequest(request.id, {
                status: newStatus,
                updatedAt: timestamp,
                history: updatedHistory
            });

            toast.success('Status updated successfully');
            setNote('');
            // Optional: reset updatedBy? Usually better to keep it if they have multiple updates
        } catch (error) {
            console.error('Error updating maintenance status:', error);
            toast.error('Failed to update status');
        } finally {
            setIsUpdating(false);
        }
    };

    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString([], {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).replace(/\//g, '-');
    };

    return (
        <Modal onClose={onClose} title="Request Details" maxWidthClass="sm:max-w-2xl">
            <div className="flex flex-col">
                <div className="px-8 pb-4 pt-2">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest leading-none bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded">
                            {request.requestId}
                        </span>
                        <div className="flex items-center gap-2">
                             <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Last Updated:</span>
                             <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-tight">{formatDateTime(request.updatedAt)}</span>
                        </div>
                    </div>
                </div>

                <div className="px-8 pb-8 space-y-8">
                    {/* Header Info */}
                    <div className="flex items-start gap-4">
                        <div className={`p-4 rounded-[24px] ${request.type === 'Room' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30' : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30'}`}>
                            {request.type === 'Room' ? <Icons.Home className="w-6 h-6" /> : <Icons.Globe className="w-6 h-6" />}
                        </div>
                        <div className="flex-grow min-w-0">
                            <h3 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight truncate">
                                {request.title}
                            </h3>
                            <p className="text-sm font-bold text-gray-400 uppercase tracking-widest mt-1">
                                {request.type === 'Room' ? `Room ${request.roomNumber}` : request.facility}
                            </p>
                        </div>
                    </div>

                    {/* Metadata Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-3xl">
                            <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 opacity-60">Status</span>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${getStatusColor(request.status)}`}>
                                {request.status}
                            </span>
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-3xl">
                            <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 opacity-60">Priority</span>
                            <span className={`text-xs font-black uppercase tracking-widest ${getPriorityColor(request.priority)}`}>
                                {request.priority}
                            </span>
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-3xl">
                            <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 opacity-60">Author</span>
                            <span className="text-sm font-bold text-gray-900 dark:text-white uppercase truncate block leading-tight">{request.createdBy}</span>
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-slate-900 rounded-3xl">
                            <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 opacity-60">Created</span>
                            <div className="text-[11px] font-bold text-gray-900 dark:text-white flex flex-col leading-tight">
                               <span>{new Date(request.createdAt).toLocaleDateString()}</span>
                            </div>
                        </div>
                    </div>

                    {/* Description Section */}
                    <div className="space-y-3">
                        <span className="block text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Fault Description</span>
                        <div className="text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-slate-900/50 p-6 rounded-[24px] leading-relaxed border border-gray-100/50 dark:border-white/5">
                            {request.description || <span className="italic opacity-40 font-bold uppercase tracking-widest text-[10px]">No description provided</span>}
                        </div>
                    </div>

                     {/* Status Update section */}
                    <div className="bg-white dark:bg-slate-900 p-8 rounded-[40px] border-2 border-indigo-50 dark:border-indigo-900/20 shadow-xl shadow-indigo-100/20 dark:shadow-none">
                        <h4 className="text-[11px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-6 flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-indigo-50 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center">
                                <Icons.RefreshCw className="w-4 h-4 animate-spin-slow" />
                            </div>
                            Update Request Status
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Your Name (Required)</label>
                                <select
                                    value={updatedBy}
                                    onChange={(e) => setUpdatedBy(e.target.value)}
                                    className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-800 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all appearance-none cursor-pointer"
                                >
                                    <option value="">Select name...</option>
                                    {staffList.sort((a, b) => a.name.localeCompare(b.name)).map(staff => (
                                        <option key={staff.email} value={staff.name}>{staff.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Transition To</label>
                                <select
                                    value={newStatus}
                                    onChange={(e) => setNewStatus(e.target.value as MaintenanceStatus)}
                                    className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-800 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all appearance-none cursor-pointer"
                                >
                                    <option value="Open">Open</option>
                                    <option value="In Progress">In Progress</option>
                                    <option value="On Hold">On Hold</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Cancelled">Cancelled</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3 px-1">Internal Note</label>
                                <textarea
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder="Provide more details about the update..."
                                    rows={3}
                                    className="w-full px-5 py-4 bg-gray-50 dark:bg-slate-800 border-none rounded-2xl text-sm font-bold focus:ring-2 focus:ring-indigo-500 transition-all resize-none"
                                />
                            </div>
                        </div>
                        <button
                            onClick={handleUpdateStatus}
                            disabled={isUpdating}
                            className="w-full py-5 rounded-[24px] font-black uppercase tracking-widest text-[12px] bg-indigo-600 text-white hover:bg-indigo-700 shadow-xl shadow-indigo-200 dark:shadow-none transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3"
                        >
                            {isUpdating ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    <span>Processing...</span>
                                </>
                            ) : (
                                <>
                                    <Icons.CheckCircle className="w-4 h-4" />
                                    <span>Confirm Status Update</span>
                                </>
                            )}
                        </button>
                    </div>

                    {/* Activity Log */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between px-1">
                            <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                <Icons.History className="w-3.5 h-3.5" />
                                Audit History
                            </h4>
                            <span className="text-[9px] font-black text-gray-300 dark:text-gray-600 uppercase tracking-widest transition-opacity">{request.history?.length || 0} Entries</span>
                        </div>
                        <div className="space-y-3 max-h-[300px] overflow-y-auto no-scrollbar pr-2 pb-2">
                            {request.history && request.history.length > 0 ? (
                                request.history.slice().reverse().map((item, idx) => (
                                    <div key={idx} className="relative pl-8 border-l border-gray-100 dark:border-slate-800 pb-4 last:pb-0">
                                        <div className="absolute left-[-4.5px] top-2 w-2 h-2 rounded-full bg-indigo-500 ring-4 ring-white dark:ring-slate-800 shadow-sm" />
                                        <div className="flex flex-col gap-1.5">
                                            <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-1">
                                                <span className="text-[10px] font-black text-gray-900 dark:text-white uppercase tracking-tight leading-none bg-gray-50 dark:bg-slate-900 px-2 py-1 rounded inline-block w-fit">
                                                    {item.action}
                                                </span>
                                                <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">
                                                    {formatDateTime(item.timestamp)}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest opacity-80">By {item.changedBy}</span>
                                            </div>
                                            {item.note && (
                                                <div className="mt-1 relative group">
                                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-200 dark:bg-indigo-900 rounded-full opacity-40" />
                                                    <p className="text-[11px] pl-3 font-medium text-gray-500 dark:text-gray-400 italic leading-relaxed">
                                                        "{item.note}"
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="py-12 bg-gray-50/50 dark:bg-slate-900/30 rounded-[24px] border-2 border-dashed border-gray-100 dark:border-slate-800 flex flex-col items-center justify-center text-center">
                                    <Icons.History className="w-8 h-8 text-gray-200 mb-2" />
                                    <p className="text-[10px] text-gray-300 uppercase font-black tracking-widest">No activity logged yet</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default MaintenanceDetailModal;
